import React, { useState, useEffect, useCallback } from 'react';
import {
    Card, Table, Tag, Button, Space, Row, Col, Input,
    Typography, Avatar, Spin, Empty, Statistic, message, Select, DatePicker, Dropdown,
    Drawer, Divider, Timeline, Descriptions, Progress, Tooltip
} from 'antd';
import {
    ReloadOutlined,
    CheckCircleOutlined, ClockCircleOutlined,
    PhoneOutlined, TeamOutlined, CrownOutlined,
    SearchOutlined, EyeOutlined,
    WalletOutlined, DollarOutlined,
    DownloadOutlined, FileExcelOutlined,
    FilterOutlined, CopyOutlined,
    UserOutlined, CalendarOutlined,
    ArrowRightOutlined, HistoryOutlined,
    FilePdfOutlined
} from '@ant-design/icons';
import affiliatesService from '../../common/services/affiliates.service';
import dayjs from 'dayjs';
import XLSX from 'xlsx-js-style';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const RevenuesPaymentStatusPage = () => {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState([]);
    const [stats, setStats] = useState({});
    const [searchText, setSearchText] = useState('');
    const [typeFilter, setTypeFilter] = useState(null);
    const [paymentFilter, setPaymentFilter] = useState(null);
    const [cityFilter, setCityFilter] = useState(null);
    const [dateRange, setDateRange] = useState(null);
    const [exporting, setExporting] = useState(false);

    // Drawer state
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [selectedBeneficiary, setSelectedBeneficiary] = useState(null);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (typeFilter) params.type = typeFilter;
            if (paymentFilter) params.paymentStatus = paymentFilter;
            if (dateRange && dateRange[0] && dateRange[1]) {
                params.startDate = dateRange[0].format('YYYY-MM-DD');
                params.endDate = dateRange[1].format('YYYY-MM-DD');
            }

            const response = await affiliatesService.getPaymentStatus(params);
            if (response.success) {
                setData(response.data || []);
                setStats(response.stats || {});
            } else {
                message.error(response.message || 'Erreur lors du chargement');
            }
        } catch (error) {
            console.error('Erreur chargement état des paiements:', error);
            message.error('Erreur lors du chargement des données');
        } finally {
            setLoading(false);
        }
    }, [typeFilter, paymentFilter, dateRange]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const cityOptions = [...new Map(
        data
            .filter(d => d.city?.name)
            .map(d => [d.city.name, { label: d.city.name, value: d.city.name }])
    ).values()].sort((a, b) => a.label.localeCompare(b.label));

    const filteredData = data.filter(item => {
        if (cityFilter && item.city?.name !== cityFilter) return false;
        if (!searchText) return true;
        const search = searchText.toLowerCase();
        return (
            (item.firstName?.toLowerCase() || '').includes(search) ||
            (item.lastName?.toLowerCase() || '').includes(search) ||
            (item.phone || '').includes(search)
        );
    });

    const prepareExportData = () => {
        return filteredData.map(item => ({
            'Type': item.type === 'affiliate' ? 'Affilié' : 'Ambassadeur',
            'Nom': `${item.firstName || ''} ${item.lastName || ''}`.trim(),
            'Téléphone': item.phone || '',
            'Ville': item.city?.name || '-',
            'Chauffeurs': item.driversCount || 0,
            'Payés': item.driversPaid || 0,
            'En attente': item.driversPending || 0,
            'Partiels': item.driversPartial || 0,
            'Gains totaux (F)': item.totalGains || 0,
            'Payé (F)': item.totalPaid || 0,
            'Reste à payer (F)': item.totalPending || 0,
            'Statut global': item.overallPaymentStatus === 'paid' ? 'Payé' :
                item.overallPaymentStatus === 'partial' ? 'Partiel' : 'En attente',
            'Réf. paiement': (item.paymentReferences || []).join(', ') || '-'
        }));
    };

    const exportToCSV = () => {
        setExporting(true);
        try {
            const exportData = prepareExportData();
            if (exportData.length === 0) {
                message.warning('Aucune donnée à exporter');
                return;
            }

            const titleLine = `État des paiements - ${typeFilter === 'affiliate' ? 'Affiliés' : typeFilter === 'ambassador' ? 'Ambassadeurs' : 'Tous'}`;
            let periodLine = '';
            if (dateRange && dateRange[0] && dateRange[1]) {
                periodLine = `Période: ${dateRange[0].format('DD/MM/YYYY')} - ${dateRange[1].format('DD/MM/YYYY')}`;
            } else {
                periodLine = `Exporté le: ${dayjs().format('DD/MM/YYYY à HH:mm')}`;
            }

            const headers = Object.keys(exportData[0]);
            const csvContent = [
                titleLine,
                periodLine,
                '',
                headers.join(';'),
                ...exportData.map(row => headers.map(h => `"${row[h]}"`).join(';'))
            ].join('\n');

            const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            const filename = `etat_paiements_${dayjs().format('YYYY-MM-DD_HHmm')}.csv`;

            link.setAttribute('href', url);
            link.setAttribute('download', filename);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            message.success(`Export CSV réussi: ${filename}`);
        } catch (error) {
            console.error('Erreur export CSV:', error);
            message.error('Erreur lors de l\'export CSV');
        } finally {
            setExporting(false);
        }
    };

    const exportToExcel = () => {
        setExporting(true);
        try {
            const exportData = prepareExportData();
            if (exportData.length === 0) {
                message.warning('Aucune donnée à exporter');
                return;
            }

            const titleStyle = {
                font: { bold: true, sz: 14, color: { rgb: "000000" } },
                alignment: { horizontal: "left", vertical: "center" }
            };
            const periodStyle = {
                font: { bold: true, sz: 11, color: { rgb: "555555" } },
                alignment: { horizontal: "left", vertical: "center" }
            };
            const headerStyle = {
                font: { bold: true, sz: 11, color: { rgb: "FFFFFF" } },
                fill: { fgColor: { rgb: "722ED1" } },
                alignment: { horizontal: "center", vertical: "center" },
                border: {
                    top: { style: "thin", color: { rgb: "000000" } },
                    bottom: { style: "thin", color: { rgb: "000000" } },
                    left: { style: "thin", color: { rgb: "000000" } },
                    right: { style: "thin", color: { rgb: "000000" } }
                }
            };
            const cellStyle = {
                border: {
                    top: { style: "thin", color: { rgb: "CCCCCC" } },
                    bottom: { style: "thin", color: { rgb: "CCCCCC" } },
                    left: { style: "thin", color: { rgb: "CCCCCC" } },
                    right: { style: "thin", color: { rgb: "CCCCCC" } }
                },
                alignment: { vertical: "center" }
            };

            const titleText = `ÉTAT DES PAIEMENTS - ${typeFilter === 'affiliate' ? 'AFFILIÉS' : typeFilter === 'ambassador' ? 'AMBASSADEURS' : 'TOUS'}`;
            const periodText = dateRange && dateRange[0] && dateRange[1]
                ? `PÉRIODE: ${dateRange[0].format('DD/MM/YYYY')} - ${dateRange[1].format('DD/MM/YYYY')}`
                : `EXPORTÉ LE: ${dayjs().format('DD/MM/YYYY à HH:mm').toUpperCase()}`;

            const headerRows = [
                [{ v: titleText, s: titleStyle }],
                [{ v: periodText, s: periodStyle }],
                []
            ];

            const worksheet = XLSX.utils.aoa_to_sheet(headerRows);

            const headers = Object.keys(exportData[0]);
            const dataWithHeaders = [
                headers.map(h => ({ v: h.toUpperCase(), s: headerStyle })),
                ...exportData.map(row => headers.map(h => ({ v: row[h], s: cellStyle })))
            ];

            XLSX.utils.sheet_add_aoa(worksheet, dataWithHeaders, { origin: 'A4' });

            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'État des paiements');

            const colWidths = headers.map(key => ({
                wch: Math.max(key.length, ...exportData.map(row => String(row[key] || '').length), 12) + 2
            }));
            worksheet['!cols'] = colWidths;

            const colCount = headers.length;
            worksheet['!merges'] = [
                { s: { r: 0, c: 0 }, e: { r: 0, c: colCount - 1 } },
                { s: { r: 1, c: 0 }, e: { r: 1, c: colCount - 1 } }
            ];

            worksheet['!rows'] = [
                { hpt: 25 },
                { hpt: 20 },
                { hpt: 15 },
                { hpt: 22 }
            ];

            const filename = `etat_paiements_${dayjs().format('YYYY-MM-DD_HHmm')}.xlsx`;
            XLSX.writeFile(workbook, filename);

            message.success(`Export Excel réussi: ${filename}`);
        } catch (error) {
            console.error('Erreur export Excel:', error);
            message.error('Erreur lors de l\'export Excel');
        } finally {
            setExporting(false);
        }
    };

    const exportMenuItems = [
        {
            key: 'csv',
            label: 'Exporter en CSV',
            icon: <DownloadOutlined />,
            onClick: exportToCSV
        },
        {
            key: 'excel',
            label: 'Exporter en Excel',
            icon: <FileExcelOutlined />,
            onClick: exportToExcel
        }
    ];

    // Ouvrir le drawer de détail
    const openDetail = (record) => {
        setSelectedBeneficiary(record);
        setDrawerOpen(true);
    };

    const getStatusConfig = (status) => {
        const config = {
            paid: { color: 'green', text: 'Payé', icon: <CheckCircleOutlined /> },
            pending: { color: 'orange', text: 'En attente', icon: <ClockCircleOutlined /> },
            partial: { color: 'blue', text: 'Partiel', icon: <WalletOutlined /> },
            none: { color: 'default', text: '-', icon: null }
        };
        return config[status] || config.none;
    };

    const columns = [
        {
            title: 'Bénéficiaire',
            key: 'beneficiary',
            width: 250,
            fixed: 'left',
            render: (_, record) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <Avatar
                        size={40}
                        style={{
                            background: record.type === 'affiliate' ? '#1890ff' : '#722ed1',
                            flexShrink: 0
                        }}
                        icon={record.type === 'affiliate' ? <TeamOutlined /> : <CrownOutlined />}
                    />
                    <div>
                        <div style={{ fontWeight: 600, color: '#262626', fontSize: 13 }}>
                            {record.firstName} {record.lastName}
                        </div>
                        <div style={{ fontSize: 11, color: '#8c8c8c' }}>
                            <PhoneOutlined style={{ marginRight: 4 }} />
                            {record.phone}
                        </div>
                        <Tag
                            color={record.type === 'affiliate' ? 'blue' : 'purple'}
                            style={{ margin: '2px 0 0 0', borderRadius: 8, fontSize: 10 }}
                        >
                            {record.type === 'affiliate' ? 'Affilié' : 'Ambassadeur'}
                        </Tag>
                    </div>
                </div>
            )
        },
        {
            title: 'Ville',
            key: 'city',
            width: 100,
            render: (_, record) => (
                <Tag style={{ margin: 0, fontSize: 11 }}>
                    {record.city?.name || '-'}
                </Tag>
            )
        },
        {
            title: 'Chauffeurs',
            key: 'drivers',
            width: 120,
            align: 'center',
            render: (_, record) => (
                <div>
                    <div style={{ fontWeight: 700, fontSize: 16, color: '#262626' }}>
                        {record.driversCount || 0}
                    </div>
                    <div style={{ fontSize: 10, color: '#8c8c8c' }}>
                        <span style={{ color: '#52c41a' }}>{record.driversPaid || 0}✓</span>
                        {' / '}
                        <span style={{ color: '#faad14' }}>{record.driversPending || 0}⏳</span>
                        {record.driversPartial > 0 && (
                            <span style={{ color: '#1890ff' }}> / {record.driversPartial}⚡</span>
                        )}
                    </div>
                </div>
            )
        },
        {
            title: 'Gains totaux',
            key: 'totalGains',
            width: 130,
            align: 'right',
            render: (_, record) => (
                <div style={{ fontWeight: 600, color: '#262626', fontSize: 14 }}>
                    {(record.totalGains || 0).toLocaleString()} F
                </div>
            ),
            sorter: (a, b) => (a.totalGains || 0) - (b.totalGains || 0)
        },
        {
            title: 'Payé',
            key: 'totalPaid',
            width: 120,
            align: 'right',
            render: (_, record) => (
                <div style={{ fontWeight: 600, color: '#52c41a', fontSize: 14 }}>
                    {(record.totalPaid || 0).toLocaleString()} F
                </div>
            ),
            sorter: (a, b) => (a.totalPaid || 0) - (b.totalPaid || 0)
        },
        {
            title: 'Reste à payer',
            key: 'totalPending',
            width: 130,
            align: 'right',
            render: (_, record) => (
                <div style={{
                    fontWeight: 700,
                    color: (record.totalPending || 0) > 0 ? '#faad14' : '#52c41a',
                    fontSize: 14
                }}>
                    {(record.totalPending || 0).toLocaleString()} F
                </div>
            ),
            sorter: (a, b) => (a.totalPending || 0) - (b.totalPending || 0),
            defaultSortOrder: 'descend'
        },
        {
            title: 'Statut',
            key: 'status',
            width: 100,
            align: 'center',
            render: (_, record) => {
                const c = getStatusConfig(record.overallPaymentStatus);
                return (
                    <Tag color={c.color} icon={c.icon} style={{ borderRadius: 10 }}>
                        {c.text}
                    </Tag>
                );
            },
            filters: [
                { text: 'Payé', value: 'paid' },
                { text: 'En attente', value: 'pending' },
                { text: 'Partiel', value: 'partial' }
            ],
            onFilter: (value, record) => record.overallPaymentStatus === value
        },
        {
            title: 'Réf. paiement',
            key: 'paymentReferences',
            width: 160,
            render: (_, record) => {
                const refs = record.paymentReferences || [];
                if (refs.length === 0) {
                    return <Text type="secondary" style={{ fontSize: 11 }}>-</Text>;
                }
                return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {refs.slice(0, 3).map((ref, idx) => (
                            <Tag
                                key={idx}
                                style={{ margin: 0, fontSize: 10, cursor: 'pointer', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                icon={<CopyOutlined />}
                                onClick={() => {
                                    navigator.clipboard.writeText(ref);
                                    message.success('Référence copiée');
                                }}
                                title={ref}
                            >
                                {ref.length > 16 ? ref.slice(0, 16) + '…' : ref}
                            </Tag>
                        ))}
                        {refs.length > 3 && (
                            <Text type="secondary" style={{ fontSize: 10 }}>
                                +{refs.length - 3} autre{refs.length - 3 > 1 ? 's' : ''}
                            </Text>
                        )}
                    </div>
                );
            }
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 80,
            align: 'center',
            fixed: 'right',
            render: (_, record) => (
                <Tooltip title="Voir le détail des paiements">
                    <Button
                        type="text"
                        icon={<EyeOutlined />}
                        onClick={() => openDetail(record)}
                        style={{ color: '#1890ff' }}
                    />
                </Tooltip>
            )
        }
    ];

    // Colonnes du tableau des chauffeurs dans le drawer
    const driverColumns = [
        {
            title: 'Chauffeur',
            key: 'driver',
            width: 180,
            render: (_, record) => (
                <div>
                    <div style={{ fontWeight: 600, fontSize: 12, color: '#262626' }}>
                        <UserOutlined style={{ marginRight: 4 }} />
                        {record.firstName} {record.lastName}
                    </div>
                    <div style={{ fontSize: 11, color: '#8c8c8c' }}>
                        <PhoneOutlined style={{ marginRight: 4 }} />
                        {record.phone}
                    </div>
                </div>
            )
        },
        {
            title: 'Type',
            key: 'driverType',
            width: 90,
            align: 'center',
            render: (_, record) => (
                <Tag
                    color={record.driverType === 'new' ? 'green' : 'orange'}
                    style={{ borderRadius: 8, fontSize: 10 }}
                >
                    {record.driverType === 'new' ? 'Nouveau' : 'Réactivation'}
                </Tag>
            )
        },
        {
            title: 'Période',
            key: 'period',
            width: 140,
            render: (_, record) => (
                <div style={{ fontSize: 11 }}>
                    <div><CalendarOutlined style={{ marginRight: 4 }} />{dayjs(record.trackingStartDate).format('DD/MM/YYYY')}</div>
                    <div style={{ color: '#8c8c8c' }}>
                        <ArrowRightOutlined style={{ marginRight: 4 }} />
                        {dayjs(record.activationWindowEnd).format('DD/MM/YYYY')}
                    </div>
                </div>
            )
        },
        {
            title: 'Gains',
            key: 'gains',
            width: 90,
            align: 'right',
            render: (_, record) => (
                <Text strong style={{ fontSize: 12 }}>{(record.gains || 0).toLocaleString()} F</Text>
            ),
            sorter: (a, b) => (a.gains || 0) - (b.gains || 0)
        },
        {
            title: 'Payé',
            key: 'paidAmount',
            width: 90,
            align: 'right',
            render: (_, record) => (
                <Text style={{ fontSize: 12, color: '#52c41a', fontWeight: 600 }}>
                    {(record.paidAmount || 0).toLocaleString()} F
                </Text>
            )
        },
        {
            title: 'Reste',
            key: 'remaining',
            width: 90,
            align: 'right',
            render: (_, record) => {
                const remaining = (record.gains || 0) - (record.paidAmount || 0);
                return (
                    <Text style={{ fontSize: 12, color: remaining > 0 ? '#faad14' : '#52c41a', fontWeight: 600 }}>
                        {remaining.toLocaleString()} F
                    </Text>
                );
            }
        },
        {
            title: 'Statut',
            key: 'paymentStatus',
            width: 100,
            align: 'center',
            render: (_, record) => {
                const c = getStatusConfig(record.paymentStatus);
                return <Tag color={c.color} icon={c.icon} style={{ borderRadius: 8, fontSize: 10 }}>{c.text}</Tag>;
            }
        }
    ];

    // Calculs pour les statistiques
    const totalGains = (stats.totalGainsAffiliates || 0) + (stats.totalGainsAmbassadors || 0);
    const totalPaid = (stats.totalPaidAffiliates || 0) + (stats.totalPaidAmbassadors || 0);
    const totalPending = (stats.totalPendingAffiliates || 0) + (stats.totalPendingAmbassadors || 0);

    // Construire la timeline de paiement pour le drawer
    const buildPaymentTimeline = (beneficiary) => {
        if (!beneficiary?.drivers) return [];

        const allPayments = [];

        for (const driver of beneficiary.drivers) {
            // Paiements depuis l'historique
            if (driver.paymentHistory && driver.paymentHistory.length > 0) {
                for (const ph of driver.paymentHistory) {
                    allPayments.push({
                        date: ph.date,
                        amount: ph.amount,
                        reference: ph.reference,
                        notes: ph.notes,
                        driverName: `${driver.firstName || ''} ${driver.lastName || ''}`.trim(),
                        driverPhone: driver.phone,
                        type: 'payment'
                    });
                }
            }
            // Si payé en une fois (pas d'historique mais paymentDate existe)
            else if (driver.paymentStatus === 'paid' && driver.paymentDate) {
                allPayments.push({
                    date: driver.paymentDate,
                    amount: driver.gains,
                    reference: (driver.paymentReferences || [])[0] || null,
                    notes: null,
                    driverName: `${driver.firstName || ''} ${driver.lastName || ''}`.trim(),
                    driverPhone: driver.phone,
                    type: 'payment'
                });
            }
        }

        // Trier par date décroissante
        allPayments.sort((a, b) => new Date(b.date) - new Date(a.date));
        return allPayments;
    };

    // Export PDF du détail d'un bénéficiaire
    const exportDetailPDF = (beneficiary) => {
        if (!beneficiary) return;
        try {
            const doc = new jsPDF();
            const benName = `${beneficiary.firstName || ''} ${beneficiary.lastName || ''}`.trim();
            const benType = beneficiary.type === 'affiliate' ? 'Affilié' : 'Ambassadeur';
            const statusText = beneficiary.overallPaymentStatus === 'paid' ? 'Payé' :
                beneficiary.overallPaymentStatus === 'partial' ? 'Partiel' : 'En attente';

            // Titre
            doc.setFontSize(16);
            doc.setFont(undefined, 'bold');
            doc.text('TIGOON Connect - Détail des paiements', 14, 18);

            // Infos bénéficiaire
            doc.setFontSize(10);
            doc.setFont(undefined, 'normal');
            doc.text(`Bénéficiaire: ${benName}`, 14, 28);
            doc.text(`Type: ${benType}`, 14, 34);
            doc.text(`Téléphone: ${beneficiary.phone || '-'}`, 14, 40);
            doc.text(`Ville: ${beneficiary.city?.name || '-'}`, 14, 46);
            doc.text(`Exporté le: ${dayjs().format('DD/MM/YYYY à HH:mm')}`, 14, 52);

            // Résumé financier
            doc.setFontSize(12);
            doc.setFont(undefined, 'bold');
            doc.text('Résumé financier', 14, 64);

            autoTable(doc, {
                startY: 68,
                head: [['Gains totaux', 'Total payé', 'Reste à payer', 'Statut', 'Chauffeurs']],
                body: [[
                    `${(beneficiary.totalGains || 0).toLocaleString()} F`,
                    `${(beneficiary.totalPaid || 0).toLocaleString()} F`,
                    `${(beneficiary.totalPending || 0).toLocaleString()} F`,
                    statusText,
                    `${beneficiary.driversCount || 0}`
                ]],
                styles: { fontSize: 9, cellPadding: 4 },
                headStyles: { fillColor: [114, 46, 209], textColor: 255, fontStyle: 'bold' },
                theme: 'grid'
            });

            // Tableau des chauffeurs
            let currentY = doc.lastAutoTable.finalY + 12;
            doc.setFontSize(12);
            doc.setFont(undefined, 'bold');
            doc.text(`Détail par chauffeur (${(beneficiary.drivers || []).length})`, 14, currentY);

            const driverRows = (beneficiary.drivers || []).map(d => {
                const remaining = (d.gains || 0) - (d.paidAmount || 0);
                const dStatus = d.paymentStatus === 'paid' ? 'Payé' :
                    d.paymentStatus === 'partial' ? 'Partiel' : 'En attente';
                return [
                    `${d.firstName || ''} ${d.lastName || ''}`.trim(),
                    d.phone || '-',
                    d.driverType === 'new' ? 'Nouveau' : 'Réactivation',
                    `${(d.gains || 0).toLocaleString()} F`,
                    `${(d.paidAmount || 0).toLocaleString()} F`,
                    `${remaining.toLocaleString()} F`,
                    dStatus
                ];
            });

            autoTable(doc, {
                startY: currentY + 4,
                head: [['Chauffeur', 'Téléphone', 'Type', 'Gains', 'Payé', 'Reste', 'Statut']],
                body: driverRows,
                styles: { fontSize: 8, cellPadding: 3 },
                headStyles: { fillColor: [114, 46, 209], textColor: 255, fontStyle: 'bold' },
                columnStyles: {
                    3: { halign: 'right' },
                    4: { halign: 'right' },
                    5: { halign: 'right' },
                    6: { halign: 'center' }
                },
                theme: 'grid'
            });

            // Historique des paiements
            const timeline = buildPaymentTimeline(beneficiary);
            if (timeline.length > 0) {
                currentY = doc.lastAutoTable.finalY + 12;

                // Vérifier s'il faut une nouvelle page
                if (currentY > 260) {
                    doc.addPage();
                    currentY = 18;
                }

                doc.setFontSize(12);
                doc.setFont(undefined, 'bold');
                doc.text(`Historique des paiements (${timeline.length})`, 14, currentY);

                const timelineRows = timeline.map(p => [
                    dayjs(p.date).format('DD/MM/YYYY HH:mm'),
                    `${(p.amount || 0).toLocaleString()} F`,
                    p.driverName || '-',
                    p.driverPhone || '-',
                    p.reference || '-',
                    p.notes || '-'
                ]);

                autoTable(doc, {
                    startY: currentY + 4,
                    head: [['Date', 'Montant', 'Chauffeur', 'Téléphone', 'Référence', 'Notes']],
                    body: timelineRows,
                    styles: { fontSize: 7, cellPadding: 3 },
                    headStyles: { fillColor: [82, 196, 26], textColor: 255, fontStyle: 'bold' },
                    columnStyles: {
                        1: { halign: 'right' }
                    },
                    theme: 'grid'
                });
            }

            const filename = `paiements_${benName.replace(/\s+/g, '_')}_${dayjs().format('YYYY-MM-DD')}.pdf`;
            doc.save(filename);
            message.success(`Export PDF réussi: ${filename}`);
        } catch (error) {
            console.error('Erreur export PDF détail:', error);
            message.error("Erreur lors de l'export PDF");
        }
    };

    // Export Excel du détail d'un bénéficiaire
    const exportDetailExcel = (beneficiary) => {
        if (!beneficiary) return;
        try {
            const benName = `${beneficiary.firstName || ''} ${beneficiary.lastName || ''}`.trim();
            const benType = beneficiary.type === 'affiliate' ? 'Affilié' : 'Ambassadeur';
            const statusText = beneficiary.overallPaymentStatus === 'paid' ? 'Payé' :
                beneficiary.overallPaymentStatus === 'partial' ? 'Partiel' : 'En attente';

            const titleStyle = { font: { bold: true, sz: 14, color: { rgb: "000000" } }, alignment: { horizontal: "left", vertical: "center" } };
            const subtitleStyle = { font: { bold: true, sz: 11, color: { rgb: "555555" } }, alignment: { horizontal: "left", vertical: "center" } };
            const headerStyle = {
                font: { bold: true, sz: 10, color: { rgb: "FFFFFF" } },
                fill: { fgColor: { rgb: "722ED1" } },
                alignment: { horizontal: "center", vertical: "center" },
                border: { top: { style: "thin", color: { rgb: "000000" } }, bottom: { style: "thin", color: { rgb: "000000" } }, left: { style: "thin", color: { rgb: "000000" } }, right: { style: "thin", color: { rgb: "000000" } } }
            };
            const headerGreenStyle = {
                ...headerStyle,
                fill: { fgColor: { rgb: "52C41A" } }
            };
            const cellStyle = {
                border: { top: { style: "thin", color: { rgb: "CCCCCC" } }, bottom: { style: "thin", color: { rgb: "CCCCCC" } }, left: { style: "thin", color: { rgb: "CCCCCC" } }, right: { style: "thin", color: { rgb: "CCCCCC" } } },
                alignment: { vertical: "center" }
            };
            const cellRightStyle = { ...cellStyle, alignment: { horizontal: "right", vertical: "center" } };

            const workbook = XLSX.utils.book_new();

            // === Feuille 1 : Résumé + Chauffeurs ===
            const rows1 = [
                [{ v: `DÉTAIL DES PAIEMENTS - ${benName.toUpperCase()}`, s: titleStyle }],
                [{ v: `${benType} | Tél: ${beneficiary.phone || '-'} | Ville: ${beneficiary.city?.name || '-'}`, s: subtitleStyle }],
                [{ v: `Exporté le: ${dayjs().format('DD/MM/YYYY à HH:mm')}`, s: subtitleStyle }],
                [],
                [
                    { v: 'GAINS TOTAUX', s: headerStyle },
                    { v: 'TOTAL PAYÉ', s: headerStyle },
                    { v: 'RESTE À PAYER', s: headerStyle },
                    { v: 'STATUT', s: headerStyle },
                    { v: 'CHAUFFEURS', s: headerStyle },
                    { v: 'PAYÉS', s: headerStyle },
                    { v: 'EN ATTENTE', s: headerStyle },
                    { v: 'PARTIELS', s: headerStyle }
                ],
                [
                    { v: `${(beneficiary.totalGains || 0).toLocaleString()} F`, s: cellStyle },
                    { v: `${(beneficiary.totalPaid || 0).toLocaleString()} F`, s: cellStyle },
                    { v: `${(beneficiary.totalPending || 0).toLocaleString()} F`, s: cellStyle },
                    { v: statusText, s: cellStyle },
                    { v: beneficiary.driversCount || 0, s: cellStyle },
                    { v: beneficiary.driversPaid || 0, s: cellStyle },
                    { v: beneficiary.driversPending || 0, s: cellStyle },
                    { v: beneficiary.driversPartial || 0, s: cellStyle }
                ],
                [],
                [{ v: 'DÉTAIL PAR CHAUFFEUR', s: titleStyle }],
                [
                    { v: 'CHAUFFEUR', s: headerStyle },
                    { v: 'TÉLÉPHONE', s: headerStyle },
                    { v: 'TYPE', s: headerStyle },
                    { v: 'PÉRIODE', s: headerStyle },
                    { v: 'GAINS', s: headerStyle },
                    { v: 'PAYÉ', s: headerStyle },
                    { v: 'RESTE', s: headerStyle },
                    { v: 'STATUT', s: headerStyle }
                ],
                ...(beneficiary.drivers || []).map(d => {
                    const remaining = (d.gains || 0) - (d.paidAmount || 0);
                    const dStatus = d.paymentStatus === 'paid' ? 'Payé' : d.paymentStatus === 'partial' ? 'Partiel' : 'En attente';
                    return [
                        { v: `${d.firstName || ''} ${d.lastName || ''}`.trim(), s: cellStyle },
                        { v: d.phone || '-', s: cellStyle },
                        { v: d.driverType === 'new' ? 'Nouveau' : 'Réactivation', s: cellStyle },
                        { v: `${dayjs(d.trackingStartDate).format('DD/MM/YY')} → ${dayjs(d.activationWindowEnd).format('DD/MM/YY')}`, s: cellStyle },
                        { v: `${(d.gains || 0).toLocaleString()} F`, s: cellRightStyle },
                        { v: `${(d.paidAmount || 0).toLocaleString()} F`, s: cellRightStyle },
                        { v: `${remaining.toLocaleString()} F`, s: cellRightStyle },
                        { v: dStatus, s: cellStyle }
                    ];
                })
            ];

            const ws1 = XLSX.utils.aoa_to_sheet(rows1);
            ws1['!cols'] = [
                { wch: 25 }, { wch: 18 }, { wch: 14 }, { wch: 22 },
                { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 14 }
            ];
            ws1['!merges'] = [
                { s: { r: 0, c: 0 }, e: { r: 0, c: 7 } },
                { s: { r: 1, c: 0 }, e: { r: 1, c: 7 } },
                { s: { r: 2, c: 0 }, e: { r: 2, c: 7 } },
                { s: { r: 7, c: 0 }, e: { r: 7, c: 7 } }
            ];
            XLSX.utils.book_append_sheet(workbook, ws1, 'Résumé & Chauffeurs');

            // === Feuille 2 : Historique des paiements ===
            const timeline = buildPaymentTimeline(beneficiary);
            const rows2 = [
                [{ v: `HISTORIQUE DES PAIEMENTS - ${benName.toUpperCase()}`, s: titleStyle }],
                [{ v: `${timeline.length} paiement${timeline.length > 1 ? 's' : ''} enregistré${timeline.length > 1 ? 's' : ''}`, s: subtitleStyle }],
                [],
                [
                    { v: 'DATE', s: headerGreenStyle },
                    { v: 'MONTANT', s: headerGreenStyle },
                    { v: 'CHAUFFEUR', s: headerGreenStyle },
                    { v: 'TÉLÉPHONE', s: headerGreenStyle },
                    { v: 'RÉFÉRENCE', s: headerGreenStyle },
                    { v: 'NOTES', s: headerGreenStyle }
                ],
                ...timeline.map(p => [
                    { v: dayjs(p.date).format('DD/MM/YYYY HH:mm'), s: cellStyle },
                    { v: `${(p.amount || 0).toLocaleString()} F`, s: cellRightStyle },
                    { v: p.driverName || '-', s: cellStyle },
                    { v: p.driverPhone || '-', s: cellStyle },
                    { v: p.reference || '-', s: cellStyle },
                    { v: p.notes || '-', s: cellStyle }
                ])
            ];

            const ws2 = XLSX.utils.aoa_to_sheet(rows2);
            ws2['!cols'] = [
                { wch: 20 }, { wch: 14 }, { wch: 25 }, { wch: 18 }, { wch: 28 }, { wch: 25 }
            ];
            ws2['!merges'] = [
                { s: { r: 0, c: 0 }, e: { r: 0, c: 5 } },
                { s: { r: 1, c: 0 }, e: { r: 1, c: 5 } }
            ];
            XLSX.utils.book_append_sheet(workbook, ws2, 'Historique paiements');

            const filename = `paiements_${benName.replace(/\s+/g, '_')}_${dayjs().format('YYYY-MM-DD')}.xlsx`;
            XLSX.writeFile(workbook, filename);
            message.success(`Export Excel réussi: ${filename}`);
        } catch (error) {
            console.error('Erreur export Excel détail:', error);
            message.error("Erreur lors de l'export Excel");
        }
    };

    if (loading && data.length === 0) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <Spin size="large" />
            </div>
        );
    }

    const ben = selectedBeneficiary;
    const paymentTimeline = ben ? buildPaymentTimeline(ben) : [];
    const progressPercent = ben && ben.totalGains > 0 ? Math.round((ben.totalPaid / ben.totalGains) * 100) : 0;

    return (
        <div style={{ padding: '16px 24px', maxWidth: 1600, margin: '0 auto' }}>
            {/* Header */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 20,
                flexWrap: 'wrap',
                gap: 12
            }}>
                <div>
                    <Title level={4} style={{ margin: 0, color: '#262626' }}>
                        <DollarOutlined style={{ marginRight: 8, color: '#722ed1' }} />
                        État des paiements
                    </Title>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        Suivi des paiements aux affiliés et ambassadeurs
                    </Text>
                </div>
                <Space wrap>
                    <Dropdown menu={{ items: exportMenuItems }} placement="bottomRight">
                        <Button icon={<DownloadOutlined />} loading={exporting} style={{ borderRadius: 8 }}>
                            Exporter
                        </Button>
                    </Dropdown>
                    <Button
                        icon={<ReloadOutlined />}
                        onClick={fetchData}
                        loading={loading}
                        style={{ borderRadius: 8 }}
                    >
                        Actualiser
                    </Button>
                </Space>
            </div>

            {/* Stats Cards */}
            <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
                <Col xs={12} sm={8} md={6} lg={4}>
                    <Card style={{ borderRadius: 12, height: '100%' }} bodyStyle={{ padding: 16, textAlign: 'center' }}>
                        <TeamOutlined style={{ fontSize: 22, color: '#1890ff', marginBottom: 4 }} />
                        <Statistic
                            value={stats.totalAffiliates || 0}
                            valueStyle={{ fontSize: 20, fontWeight: 700, color: '#262626' }}
                        />
                        <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: -4 }}>Affiliés</div>
                    </Card>
                </Col>
                <Col xs={12} sm={8} md={6} lg={4}>
                    <Card style={{ borderRadius: 12, height: '100%' }} bodyStyle={{ padding: 16, textAlign: 'center' }}>
                        <CrownOutlined style={{ fontSize: 22, color: '#722ed1', marginBottom: 4 }} />
                        <Statistic
                            value={stats.totalAmbassadors || 0}
                            valueStyle={{ fontSize: 20, fontWeight: 700, color: '#262626' }}
                        />
                        <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: -4 }}>Ambassadeurs</div>
                    </Card>
                </Col>
                <Col xs={12} sm={8} md={6} lg={4}>
                    <Card style={{ borderRadius: 12, height: '100%' }} bodyStyle={{ padding: 16, textAlign: 'center' }}>
                        <WalletOutlined style={{ fontSize: 22, color: '#faad14', marginBottom: 4 }} />
                        <Statistic
                            value={totalGains}
                            suffix="F"
                            valueStyle={{ fontSize: 18, fontWeight: 700, color: '#262626' }}
                        />
                        <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: -4 }}>Gains totaux</div>
                    </Card>
                </Col>
                <Col xs={12} sm={8} md={6} lg={4}>
                    <Card style={{ borderRadius: 12, height: '100%' }} bodyStyle={{ padding: 16, textAlign: 'center' }}>
                        <CheckCircleOutlined style={{ fontSize: 22, color: '#52c41a', marginBottom: 4 }} />
                        <Statistic
                            value={totalPaid}
                            suffix="F"
                            valueStyle={{ fontSize: 18, fontWeight: 700, color: '#52c41a' }}
                        />
                        <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: -4 }}>Total payé</div>
                    </Card>
                </Col>
                <Col xs={12} sm={8} md={6} lg={4}>
                    <Card style={{ borderRadius: 12, height: '100%' }} bodyStyle={{ padding: 16, textAlign: 'center' }}>
                        <ClockCircleOutlined style={{ fontSize: 22, color: '#faad14', marginBottom: 4 }} />
                        <Statistic
                            value={totalPending}
                            suffix="F"
                            valueStyle={{ fontSize: 18, fontWeight: 700, color: '#faad14' }}
                        />
                        <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: -4 }}>Reste à payer</div>
                    </Card>
                </Col>
                <Col xs={12} sm={8} md={6} lg={4}>
                    <Card style={{ borderRadius: 12, height: '100%' }} bodyStyle={{ padding: 16, textAlign: 'center' }}>
                        <DollarOutlined style={{ fontSize: 22, color: '#722ed1', marginBottom: 4 }} />
                        <Statistic
                            value={totalGains > 0 ? ((totalPaid / totalGains) * 100).toFixed(1) : 0}
                            suffix="%"
                            valueStyle={{ fontSize: 18, fontWeight: 700, color: '#722ed1' }}
                        />
                        <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: -4 }}>Taux payé</div>
                    </Card>
                </Col>
            </Row>

            {/* Table */}
            <Card
                style={{ borderRadius: 12 }}
                title={
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                        <span style={{ fontWeight: 600, fontSize: 15 }}>
                            <FilterOutlined style={{ marginRight: 8 }} />
                            Liste des bénéficiaires
                            <Tag style={{ marginLeft: 8, borderRadius: 12 }}>{filteredData.length}</Tag>
                        </span>
                        <Space wrap>
                            <Select
                                placeholder="Type"
                                value={typeFilter}
                                onChange={(value) => setTypeFilter(value)}
                                allowClear
                                size="small"
                                style={{ width: 130 }}
                                options={[
                                    { value: 'affiliate', label: 'Affiliés' },
                                    { value: 'ambassador', label: 'Ambassadeurs' }
                                ]}
                            />
                            <Select
                                placeholder="Statut paiement"
                                value={paymentFilter}
                                onChange={(value) => setPaymentFilter(value)}
                                allowClear
                                size="small"
                                style={{ width: 140 }}
                                options={[
                                    { value: 'paid', label: '✅ Payé' },
                                    { value: 'pending', label: '⏳ En attente' },
                                    { value: 'partial', label: '⚡ Partiel' }
                                ]}
                            />
                            {cityOptions.length >= 1 && (
                                <Select
                                    placeholder="Ville"
                                    value={cityFilter}
                                    onChange={(value) => setCityFilter(value)}
                                    allowClear
                                    size="small"
                                    style={{ width: 130 }}
                                    options={cityOptions}
                                />
                            )}
                            <RangePicker
                                placeholder={['Début', 'Fin']}
                                format="DD/MM/YYYY"
                                value={dateRange}
                                onChange={(dates) => setDateRange(dates)}
                                allowClear
                                size="small"
                                style={{ width: 220 }}
                                presets={[
                                    { label: 'Ce mois', value: [dayjs().startOf('month'), dayjs().endOf('month')] },
                                    { label: 'Mois dernier', value: [dayjs().subtract(1, 'month').startOf('month'), dayjs().subtract(1, 'month').endOf('month')] },
                                    { label: '3 derniers mois', value: [dayjs().subtract(3, 'month').startOf('month'), dayjs()] }
                                ]}
                            />
                            <Input
                                placeholder="Rechercher..."
                                prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                style={{ width: 180, borderRadius: 8 }}
                                allowClear
                                size="small"
                            />
                        </Space>
                    </div>
                }
                bodyStyle={{ padding: 0 }}
            >
                <Table
                    columns={columns}
                    dataSource={filteredData}
                    rowKey="_id"
                    loading={loading}
                    pagination={{
                        pageSize: 20,
                        showSizeChanger: true,
                        pageSizeOptions: ['10', '20', '50', '100'],
                        showTotal: (total) => `${total} bénéficiaire${total > 1 ? 's' : ''}`,
                        size: 'small',
                        style: { marginRight: 16 }
                    }}
                    scroll={{ x: 1400 }}
                    locale={{ emptyText: <Empty description="Aucun bénéficiaire trouvé" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
                    size="middle"
                />
            </Card>

            {/* Drawer Détail Paiement */}
            <Drawer
                title={
                    ben ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <Avatar
                                size={44}
                                style={{ background: ben.type === 'affiliate' ? '#1890ff' : '#722ed1', flexShrink: 0 }}
                                icon={ben.type === 'affiliate' ? <TeamOutlined /> : <CrownOutlined />}
                            />
                            <div>
                                <div style={{ fontWeight: 700, fontSize: 16 }}>
                                    {ben.firstName} {ben.lastName}
                                </div>
                                <div style={{ fontSize: 12, color: '#8c8c8c' }}>
                                    <PhoneOutlined style={{ marginRight: 4 }} />{ben.phone}
                                    <Tag
                                        color={ben.type === 'affiliate' ? 'blue' : 'purple'}
                                        style={{ marginLeft: 8, borderRadius: 8, fontSize: 10 }}
                                    >
                                        {ben.type === 'affiliate' ? 'Affilié' : 'Ambassadeur'}
                                    </Tag>
                                </div>
                            </div>
                        </div>
                    ) : 'Détail des paiements'
                }
                placement="right"
                width={720}
                open={drawerOpen}
                onClose={() => { setDrawerOpen(false); setSelectedBeneficiary(null); }}
                styles={{ body: { padding: '16px 24px' } }}
                extra={
                    ben && (
                        <Space>
                            <Tooltip title="Exporter en PDF">
                                <Button
                                    icon={<FilePdfOutlined />}
                                    size="small"
                                    onClick={() => exportDetailPDF(ben)}
                                    style={{ borderRadius: 6, color: '#ff4d4f', borderColor: '#ff4d4f' }}
                                >
                                    PDF
                                </Button>
                            </Tooltip>
                            <Tooltip title="Exporter en Excel">
                                <Button
                                    icon={<FileExcelOutlined />}
                                    size="small"
                                    onClick={() => exportDetailExcel(ben)}
                                    style={{ borderRadius: 6, color: '#52c41a', borderColor: '#52c41a' }}
                                >
                                    Excel
                                </Button>
                            </Tooltip>
                        </Space>
                    )
                }
            >
                {ben && (
                    <>
                        {/* Résumé financier */}
                        <Card
                            style={{ borderRadius: 12, marginBottom: 20, background: 'linear-gradient(135deg, #f5f5f5 0%, #fafafa 100%)' }}
                            bodyStyle={{ padding: 16 }}
                        >
                            <Row gutter={[16, 12]}>
                                <Col span={8}>
                                    <Statistic
                                        title={<span style={{ fontSize: 11 }}>Gains totaux</span>}
                                        value={ben.totalGains || 0}
                                        suffix="F"
                                        valueStyle={{ fontSize: 18, fontWeight: 700, color: '#262626' }}
                                    />
                                </Col>
                                <Col span={8}>
                                    <Statistic
                                        title={<span style={{ fontSize: 11 }}>Total payé</span>}
                                        value={ben.totalPaid || 0}
                                        suffix="F"
                                        valueStyle={{ fontSize: 18, fontWeight: 700, color: '#52c41a' }}
                                    />
                                </Col>
                                <Col span={8}>
                                    <Statistic
                                        title={<span style={{ fontSize: 11 }}>Reste à payer</span>}
                                        value={ben.totalPending || 0}
                                        suffix="F"
                                        valueStyle={{ fontSize: 18, fontWeight: 700, color: (ben.totalPending || 0) > 0 ? '#faad14' : '#52c41a' }}
                                    />
                                </Col>
                            </Row>
                            <div style={{ marginTop: 12 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                    <Text style={{ fontSize: 11, color: '#8c8c8c' }}>Progression des paiements</Text>
                                    <Text style={{ fontSize: 11, fontWeight: 600 }}>{progressPercent}%</Text>
                                </div>
                                <Progress
                                    percent={progressPercent}
                                    showInfo={false}
                                    strokeColor={progressPercent === 100 ? '#52c41a' : '#1890ff'}
                                    trailColor="#f0f0f0"
                                    size="small"
                                />
                            </div>
                        </Card>

                        {/* Résumé chauffeurs */}
                        <Descriptions
                            size="small"
                            column={3}
                            style={{ marginBottom: 20 }}
                            bordered
                        >
                            <Descriptions.Item label="Chauffeurs">{ben.driversCount || 0}</Descriptions.Item>
                            <Descriptions.Item label="Ville">{ben.city?.name || '-'}</Descriptions.Item>
                            <Descriptions.Item label="Statut">
                                {(() => { const c = getStatusConfig(ben.overallPaymentStatus); return <Tag color={c.color} icon={c.icon} style={{ borderRadius: 8 }}>{c.text}</Tag>; })()}
                            </Descriptions.Item>
                            <Descriptions.Item label="Payés">
                                <Text style={{ color: '#52c41a', fontWeight: 600 }}>{ben.driversPaid || 0}</Text>
                            </Descriptions.Item>
                            <Descriptions.Item label="En attente">
                                <Text style={{ color: '#faad14', fontWeight: 600 }}>{ben.driversPending || 0}</Text>
                            </Descriptions.Item>
                            <Descriptions.Item label="Partiels">
                                <Text style={{ color: '#1890ff', fontWeight: 600 }}>{ben.driversPartial || 0}</Text>
                            </Descriptions.Item>
                        </Descriptions>

                        {/* Historique des paiements (Timeline) */}
                        <Divider orientation="left" style={{ fontSize: 14, fontWeight: 600 }}>
                            <HistoryOutlined style={{ marginRight: 6 }} />
                            Historique des paiements
                        </Divider>

                        {paymentTimeline.length > 0 ? (
                            <Timeline
                                style={{ marginTop: 16 }}
                                items={paymentTimeline.map((payment, idx) => ({
                                    color: 'green',
                                    children: (
                                        <Card
                                            key={idx}
                                            size="small"
                                            style={{ borderRadius: 10, borderLeft: '3px solid #52c41a' }}
                                            bodyStyle={{ padding: '10px 14px' }}
                                        >
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                                <div>
                                                    <Text strong style={{ fontSize: 14, color: '#52c41a' }}>
                                                        {(payment.amount || 0).toLocaleString()} F
                                                    </Text>
                                                    <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 2 }}>
                                                        <UserOutlined style={{ marginRight: 4 }} />
                                                        {payment.driverName}
                                                        {payment.driverPhone && (
                                                            <span style={{ marginLeft: 6 }}>({payment.driverPhone})</span>
                                                        )}
                                                    </div>
                                                    {payment.reference && (
                                                        <div style={{ marginTop: 4 }}>
                                                            <Tag
                                                                style={{ fontSize: 10, cursor: 'pointer', borderRadius: 6 }}
                                                                icon={<CopyOutlined />}
                                                                onClick={() => {
                                                                    navigator.clipboard.writeText(payment.reference);
                                                                    message.success('Référence copiée');
                                                                }}
                                                                title={payment.reference}
                                                            >
                                                                Réf: {payment.reference.length > 24 ? payment.reference.slice(0, 24) + '…' : payment.reference}
                                                            </Tag>
                                                        </div>
                                                    )}
                                                    {payment.notes && (
                                                        <div style={{ fontSize: 11, color: '#595959', marginTop: 4, fontStyle: 'italic' }}>
                                                            {payment.notes}
                                                        </div>
                                                    )}
                                                </div>
                                                <Text style={{ fontSize: 11, color: '#8c8c8c', whiteSpace: 'nowrap', marginLeft: 12 }}>
                                                    <CalendarOutlined style={{ marginRight: 4 }} />
                                                    {dayjs(payment.date).format('DD/MM/YYYY HH:mm')}
                                                </Text>
                                            </div>
                                        </Card>
                                    )
                                }))}
                            />
                        ) : (
                            <Empty
                                description="Aucun paiement effectué"
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                style={{ margin: '20px 0' }}
                            />
                        )}

                        {/* Tableau des chauffeurs */}
                        <Divider orientation="left" style={{ fontSize: 14, fontWeight: 600 }}>
                            <TeamOutlined style={{ marginRight: 6 }} />
                            Détail par chauffeur ({(ben.drivers || []).length})
                        </Divider>

                        <Table
                            columns={driverColumns}
                            dataSource={ben.drivers || []}
                            rowKey={(record) => record._id || record.phone}
                            pagination={false}
                            size="small"
                            scroll={{ x: 700 }}
                            style={{ marginTop: 12 }}
                            expandable={{
                                expandedRowRender: (record) => {
                                    const history = record.paymentHistory || [];
                                    if (history.length === 0) {
                                        return <Text type="secondary" style={{ fontSize: 11 }}>Aucun paiement enregistré pour ce chauffeur</Text>;
                                    }
                                    return (
                                        <div style={{ padding: '8px 0' }}>
                                            <Text strong style={{ fontSize: 12, marginBottom: 8, display: 'block' }}>
                                                Paiements ({history.length})
                                            </Text>
                                            {history.map((ph, idx) => (
                                                <div
                                                    key={idx}
                                                    style={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center',
                                                        padding: '6px 12px',
                                                        background: idx % 2 === 0 ? '#fafafa' : '#fff',
                                                        borderRadius: 6,
                                                        marginBottom: 4
                                                    }}
                                                >
                                                    <div>
                                                        <Text strong style={{ color: '#52c41a', fontSize: 12 }}>
                                                            {(ph.amount || 0).toLocaleString()} F
                                                        </Text>
                                                        {ph.reference && (
                                                            <Tag
                                                                style={{ marginLeft: 8, fontSize: 10, cursor: 'pointer', borderRadius: 6 }}
                                                                icon={<CopyOutlined />}
                                                                onClick={() => {
                                                                    navigator.clipboard.writeText(ph.reference);
                                                                    message.success('Référence copiée');
                                                                }}
                                                            >
                                                                {ph.reference.length > 20 ? ph.reference.slice(0, 20) + '…' : ph.reference}
                                                            </Tag>
                                                        )}
                                                        {ph.notes && (
                                                            <Text style={{ fontSize: 10, color: '#8c8c8c', marginLeft: 8 }}>{ph.notes}</Text>
                                                        )}
                                                    </div>
                                                    <Text style={{ fontSize: 11, color: '#8c8c8c' }}>
                                                        {dayjs(ph.date).format('DD/MM/YYYY HH:mm')}
                                                    </Text>
                                                </div>
                                            ))}
                                        </div>
                                    );
                                },
                                rowExpandable: (record) => (record.paymentHistory || []).length > 0 || record.paymentStatus !== 'pending'
                            }}
                        />
                    </>
                )}
            </Drawer>
        </div>
    );
};

export default RevenuesPaymentStatusPage;
